/**
 * Operator LABELS — the English an operator reads as, per column type.
 *
 * One table, three readers. The editor's dropdowns always know the column type (from the leaf's Row)
 * and pair each label with a value control; `ui/.../lib/operators.ts` keeps the ORDER and the control,
 * and takes the label from here. `rows.ts` renders a leaf's business sentence with the same table and
 * the same resolved type. The SQL comment writer (`../sql-comment.ts`) has no sketch, so it infers a
 * type and falls back to the flat default.
 *
 * A flat default plus per-type overrides, because one operator usually reads the same everywhere and
 * occasionally does not: `gt` is "greater than" on a number and "after" on a timestamp. The default is
 * also what answers for a pairing no type offers — a `gt` whose value is a date STRING infers
 * `string`, and no string override exists.
 *
 * This table is presentation, not grammar. Which operators a type ACCEPTS is `operators-by-type.ts`,
 * and that stays the one authority — `presentableFor` in the editor filters against it, so a label
 * written here for a pairing the grammar rejects never reaches a dropdown.
 *
 * Shared leaf: imports two types and nothing else, so the browser can read it.
 */
import type { AttributeDataType } from '../data-type.js';
import type { OperatorName } from './names.js';

/**
 * Per-type OVERRIDES only — an operator appears here exactly when its type reads differently from
 * {@link DEFAULT_OPERATOR_LABELS}. `gt` is "greater than" everywhere except a timestamp, where it is
 * "after", so `gt` appears under `timestamp` and nowhere else.
 *
 * Overrides rather than a full per-type table because the full table was 37 entries of which 31
 * repeated the default verbatim, with nothing comparing the two. `operator-labels.test.ts` now fails
 * on an override that merely restates the default, so the duplication cannot creep back.
 *
 * Deliberately `Partial` and deliberately NOT `as const`: every read indexes by a general
 * `OperatorName`, so the value type must admit `undefined` rather than narrow to the literal keys each
 * object happens to carry.
 */
export const OPERATOR_LABEL_OVERRIDES: Record<AttributeDataType, Partial<Record<OperatorName, string>>> = {
  string: {},
  number: {},
  boolean: {},
  // A date reads as a position in time, not as a magnitude.
  timestamp: {
    eq: 'on',
    neq: 'not on',
    lt: 'before',
    lte: 'on or before',
    gt: 'after',
    gte: 'on or after',
  },
};

/**
 * The type-free label for every operator — TOTAL over `OperatorName`, which is what lets
 * {@link operatorLabel} promise a string. A new operator name therefore fails to compile here until
 * someone writes its English, rather than rendering a comment that names the raw key.
 */
export const DEFAULT_OPERATOR_LABELS = {
  eq: 'is',
  neq: 'is not',
  gt: 'greater than',
  gte: 'greater than or equal to',
  lt: 'less than',
  lte: 'less than or equal to',
  btw: 'between',
  in: 'is one of',
  nin: 'is not one of',
  // "matches pattern", NOT "contains". The compiler binds the operand verbatim — `like` emits
  // `col LIKE <value>` with an `ESCAPE` clause and wraps nothing — so a user who types `acme` gets an
  // EXACT match, and one who types `%acme%` gets the substring match. The value has always been a
  // pattern. "contains" described a wrapping the compiler never did, and ACT2-600 promoted that wrong
  // English into SQL a customer's reviewer reads as authoritative. Relabelled rather than wrapped:
  // wrapping would change who every stored `like` audience matches.
  like: 'matches pattern',
  nlike: 'does not match pattern',
  null: 'is not set',
  nnull: 'is set',
  inlast: 'in the last',
  notinlast: 'not in the last',
  innext: 'in the next',
} as const satisfies Record<OperatorName, string>;

/** The label for `operator`, preferring `type`'s override when there is one. Always returns a string. */
export function operatorLabel(operator: OperatorName, type?: AttributeDataType): string {
  return (
    (type === undefined ? undefined : OPERATOR_LABEL_OVERRIDES[type][operator]) ?? DEFAULT_OPERATOR_LABELS[operator]
  );
}
